import Axios from "axios";
import cloneDeep from "lodash/cloneDeep";

import alertCall from "../../components/@self/CustomAlert";

import { getUrl } from "./Config";
import { getDecryptedItem, setEncryptedItem } from "../encryptedStorage";
import { store } from "../../redux/storeConfig/store";

const CONFIG = getUrl();
const CONFIG_URL = CONFIG.URL;
const CORS_WORKAROUND = ""; //'https://cors-anywhere.herokuapp.com/';

// const LOGS_BASEURL = CONFIG_URL.LOGS.GET_LOGS_URL;
const AUTH0_BASE_URL = CONFIG_URL.AUTH0.BASE_URL;
const CHATBOT_BASE_URL = CONFIG_URL.CHATBOT.BASE_URL;
// const KNOWLEDGE_BASE_URL = CONFIG_URL.KNOWLEDGE_BASE.BASE_URL;
const AZURE_BLOB_UPLOAD_URL = CONFIG_URL.AZURE_BLOB.BASE_URL;
const NEW_BASE_APPLICATION = CONFIG.URL.UPLOAD_FILE;

// Token renewal state
let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

// Helper to process queued requests
const axiosUmProcessQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

const api = Axios.create();

// api request interceptor
api.interceptors.request.use(
  (config) => {
    const token = getDecryptedItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// api response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            originalRequest.transformResponse = [
              (newData) => {
                try {
                  return JSON.parse(newData);
                } catch (error) {
                  return newData;
                }
              },
            ];

            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        // const refreshToken = getDecryptedItem("refreshToken");
        const oldToken = getDecryptedItem("token");

        refreshPromise = getRefreshTokenApi(oldToken);
        const response = await refreshPromise;

        const { status, data, jsonData, errorMessage } = response;

        if (status) {
          const tokenData = jsonData || data;

          if (tokenData) {
            setEncryptedItem("token", tokenData);

            isRefreshing = false;
            axiosUmProcessQueue(null, tokenData);

            originalRequest.headers["Authorization"] = `Bearer ${tokenData}`;
            originalRequest.transformResponse = [
              (newData) => {
                try {
                  return JSON.parse(newData);
                } catch (error) {
                  return newData;
                }
              },
            ];

            return api(originalRequest);
          } else {
            // force logout
            handleSessionExpiration();

            return Promise.reject(errorMessage);
          }
        } else {
          // force logout
          handleSessionExpiration();

          return Promise.reject(errorMessage);
        }
      } catch (error) {
        // force logout
        handleSessionExpiration();

        return Promise.reject(error);
      }
    } else if (error.response?.status === 403) {
      handleSessionExpiration();

      return Promise.reject(error);
    }

    return Promise.reject(
      (error.response && error.response.data) || "Wrong Services"
    );
  }
);

const handleSessionExpiration = () => {
  forceLogOut();
  // localStorage.clear()
  // window.location.reload(true)
};

///////////////////////////////////
const getHeaderData = (customOptionsObj = {}) => {
  const customOptions = customOptionsObj || {};
  const headers = {};

  const token = getDecryptedItem("token");
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (getDecryptedItem("orgName")) {
    const userData = JSON.parse(getDecryptedItem("data"));
    const userInfo = userData;

    if (userInfo) {
      const {
        username,
        organisationId,
        organisationName,
        name,
        id: userId,
      } = userInfo;
      if (username) {
        headers.user_email = username || "";
      }
      if (userId) {
        headers.user_id = userId || "";
      }

      headers.user_org = organisationId;
      // headers.organisationId = organisationId;

      // if (organisationId) {
      //   headers.user_org = organisationId
      //   headers.organisationId = organisationId
      // } else if (getDecryptedItem("loggedInUserRole") === "Customer") {
      //   headers.user_org = userId || ""
      //   headers.organisationId = userId || ""
      // } else if (getDecryptedItem("loggedInUserRole") === "Partner") {
      //   headers.user_org = ""
      //   headers.organisationId = ""
      // } else if (getDecryptedItem("loggedInUserRole") === "Super Admin") {
      //   headers.organisationId = ""
      //   headers.user_org = ""
      // } else {
      //   headers.organisationId = ""
      //   headers.user_org = ""
      // }

      headers.username = name || "";
    }

    return {
      ...customOptions,
      headers: {
        ...headers,
        ...cloneDeep(customOptions.headers || {}),
      },
    };
  }

  return {
    ...customOptions,
    headers: {
      ...headers,
      ...cloneDeep(customOptions.headers || {}),
    },
  };
};

function get(url, customOptionsObj) {
  if (!url) {
    console.error("URL is a mandatory field to make an API call");
    return;
  }

  const head_data = getHeaderData(customOptionsObj);

  return new Promise((resolve, reject) => {
    api.get(url, head_data).then(
      (data) => {
        resolve(data);
      },
      (e) => {
        reject(e);
      }
    );
  });
}

function post(url, param, customOptionsObj) {
  if (!url) {
    console.error("URL is a mandatory field to make an API call");
    return;
  }

  const head_data = getHeaderData(customOptionsObj);

  return new Promise((resolve, reject) => {
    api.post(url, param, head_data).then(
      (resp) => {
        resolve(resp);
      },
      (e) => {
        reject(e);
      }
    );
  });
}

function put(url, param) {
  if (!url) {
    console.error("URL is a mandatory field to make an API call");
    return;
  }

  const head_data = getHeaderData();

  return new Promise((resolve, reject) => {
    api.put(url, param, head_data).then(
      (resp) => {
        resolve(resp);
      },
      (e) => {
        reject(e);
      }
    );
  });
}

function deleteApi(url, param, customOptionsObj) {
  if (!url) {
    console.error("URL is a mandatory field to make an API call");
    return;
  }

  const head_data = getHeaderData(customOptionsObj);

  return new Promise((resolve, reject) => {
    api.delete(url, { ...head_data, data: param }).then(
      (resp) => {
        resolve(resp);
      },
      (e) => {
        reject(e);
      }
    );
  });
}

const getRefreshTokenApi = async (oldToken) => {
  const response = await api.get(`${AUTH0_BASE_URL}/api/v1/access/renew-token`, {
    headers: {
      Authorization: `Bearer ${oldToken}`,
    },
  });

  return response.data;
};

///////////////////////////////////

function getCollections(orgId) {
  let url = CORS_WORKAROUND + CONFIG_URL.COLLECTIONS.GET;
  const userData = JSON.parse(getDecryptedItem("data"));
  const userInfo = userData;
  let header_data_lock = {
    username: userInfo.email,
    user_org: orgId || userInfo.organisationId,
  };

  return new Promise((resolve, reject) => {
    get(url, { headers: { header_data_lock } }).then(
      (response) => {
        resolve(response.data);
      },
      (e) => {
        reject(e);
      }
    );
  });
}

// function getFilters(id) {
//   let url = CORS_WORKAROUND + CONFIG_URL.FILTERS.GET;
//   url = url.replace("$id", id);
//   return new Promise((res, rej) => {
//     get(url).then(
//       (resp) => {
//         let data = resp.data;
//         res(data);
//       },
//       (e) => {
//         rej(e);
//       }
//     );
//   });
// }

function getDocuments(id, param) {
  param = param || "";
  let url = CORS_WORKAROUND + CONFIG_URL.DOCUMENTS.GET + param;
  url = url.replace("$id", id);
  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        let data = resp.data;
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
}

// function getEntities(cid, did) {
//   let url = CORS_WORKAROUND + CONFIG_URL.ENTITIES.GET;
//   url = url.replace("$cid", cid).replace("$did", did);
//   return new Promise((res, rej) => {
//     get(url).then(
//       (resp) => {
//         let data = resp.data;
//         res(data);
//       },
//       (e) => {
//         rej(e);
//       }
//     );
//   });
// }

function getDocument(cid, did) {
  let url = CORS_WORKAROUND + CONFIG_URL.DOCUMENT.GET;
  url = url.replace("$cid", cid).replace("$did", did);
  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        let data = resp.data;
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
}

// function getMappings(cid, did) {
//   let url = CORS_WORKAROUND + CONFIG_URL.MAPPINGS.GET;
//   url = url.replace("$cid", cid).replace("$did", did);
//   return new Promise((res, rej) => {
//     get(url).then(
//       (resp) => {
//         let data = resp.data;
//         res(data);
//       },
//       (e) => {
//         rej(e);
//       }
//     );
//   });
// }

// function getGroups() {
//   let url = CORS_WORKAROUND + CONFIG_URL.GROUPS.GET;
//   return new Promise((res, rej) => {
//     get(url).then(
//       (resp) => {
//         let data = resp.data;
//         res(data);
//       },
//       (e) => {
//         rej(e);
//       }
//     );
//   });
// }

function uploadDoc(cid, groupName, fileData) {
  let url = CORS_WORKAROUND + CONFIG_URL.DOCUMENT.POST;
  if (groupName) {
    url += "?group=" + groupName;
  }
  url = url.replace("$cid", cid);

  return new Promise((res, rej) => {
    post(url, fileData).then(
      (resp) => {
        let data = resp.data;
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
}

// function updateEntities(cid, did, param) {
//   let url = CORS_WORKAROUND + CONFIG_URL.ENTITIES.PUT;
//   url = url.replace("$cid", cid).replace("$did", did);
//   return new Promise((res, rej) => {
//     put(url, param).then(
//       (resp) => {
//         let data = resp.data;
//         res(data);
//       },
//       (e) => {
//         rej(e);
//       }
//     );
//   });
// }

function syncGoogleDrive(cid, did, workspaceName, workspaceId, organisationId) {
  let url = CONFIG_URL.SYNCGOOGLEDRIVE.POST;
  let param = {
    collectionId: cid,
    documentId: did,
    workspaceName: workspaceName,
    workspaceId: workspaceId,
    organisationId: organisationId,
  };
  return new Promise((res, rej) => {
    post(url, param).then(
      (resp) => {
        let data = resp.data;
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
}

function updateMappings(cid, did, type, param) {
  let url = CORS_WORKAROUND + CONFIG_URL.MAPPINGS.POST;
  url = url.replace("$cid", cid).replace("$did", did);
  url += `?mode=${type || "save"}`;

  return new Promise((res, rej) => {
    post(url, param).then(
      (resp) => {
        let data = resp.data;
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
}

function deleteDocument(cid, did) {
  let url = CORS_WORKAROUND + CONFIG_URL.DOCUMENT.DELETE;
  url = url.replace("$cid", cid).replace("$did", did);
  return new Promise((res, rej) => {
    deleteApi(url).then(
      (resp) => {
        let data = resp.data;
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
}

/**
 * All APIs related to the partner goes here
 */
const savePartner = (data) => {
  let url = `${AUTH0_BASE_URL}/api/partner/add`;
  return new Promise((res, rej) => {
    post(url, data).then(
      (resp) => {
        res(resp.data);
      },
      (e) => {
        rej(e);
      }
    );
  });
};

const getPartners = () => {
  let url = `${AUTH0_BASE_URL}/api/partner/listAll`;
  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        res(resp.data);
      },
      (e) => {
        rej(e);
      }
    );
  });
};

const deletePartner = (data) => {
  let id = data.id;
  let url = `${AUTH0_BASE_URL}/api/partner/delete/${id}`;
  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        res(resp.data);
      },
      (e) => {
        rej(e);
      }
    );
  });
};

/**
 * All APIs related to the user Login goes here
 */

const userLogin = (data) => {
  let url = `${AUTH0_BASE_URL}/api/v1/access/login`;
  return new Promise((res, rej) => {
    post(url, data).then(
      (resp) => {
        res(resp.data);
      },
      (e) => {
        rej(e);
      }
    );
  });
};

const getOrgDetails = (id, role) => {
  var url;
  if (role === "CustomerAdminSetup") {
    url = `${CONFIG_URL.AUTH0.BASE_URL}/api/partner/${id}`;
  } else {
    url = `${CONFIG_URL.AUTH0.BASE_URL}/api/customer/${id}`;
  }
  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        let data = resp.data;
        setEncryptedItem("orgName", data.name);
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
};
/**
 * APIs For user Image.....
 */

const uploadUserImg = (file, isProfile, organisationId, userId) => {
  var formData = new FormData();
  formData.append("image ", file, file.name);

  let url = AUTH0_BASE_URL + "/api/file/server/upload";
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
  };
  return new Promise((resolve, reject) => {
    post(
      url,
      formData,
      { params: { isProfile, organisationId, userId } },
      config
    ).then(
      (response) => {
        console.log("response", response);
        const { status, jsonData } = response?.data || {};
        if (status && jsonData) {
          resolve(`${AUTH0_BASE_URL}/api/file/server/download/${jsonData}`);
        }
        resolve(false);
      },
      (e) => {
        reject(e);
      }
    );
  });
};

const removeUserImg = (imageURL) => {
  const imageId = imageURL
    .replace(`${AUTH0_BASE_URL}/api/photo/`, "")
    .replace(`${AUTH0_BASE_URL}/api/file/server/download/`, "");

  return new Promise((resolve, reject) => {
    let url = AUTH0_BASE_URL + `/api/file/server/delete/${imageId}`;
    deleteApi(url).then(
      (response) => {
        resolve(response.data);
      },
      (e) => {
        reject(e);
      }
    );
  });
};

const changePasswordApi = (email) => {
  return new Promise((resolve, reject) => {
    get(`${AUTH0_BASE_URL}/api/v1/access/changePassword`, {
      params: { email },
    }).then(
      (response) => {
        resolve(response.data);
      },
      (e) => {
        reject(e);
      }
    );
  });
};

// function getLogs(params, userOrgId) {
//   params = params ? "&" + params : "";
//   let url = LOGS_BASEURL + "/logs?size=200" + params;
//   return new Promise((res, rej) => {
//     get(url, { headers: { user_org: userOrgId } }).then(
//       (resp) => {
//         let data = resp.data;
//         res(data);
//       },
//       (e) => {
//         rej(e);
//       }
//     );
//   });
// }

// function getLogFilters(userOrgId) {
//   let url = LOGS_BASEURL + "/filters";
//   return new Promise((res, rej) => {
//     get(url, { headers: { user_org: userOrgId } }).then(
//       (resp) => {
//         let data = resp.data;
//         res(data);
//       },
//       (e) => {
//         //change when API fixed
//         // const sampleData = {
//         //     username: ["abc.com", "xyz.com"],
//         //     resource: ["WorkItem"],
//         //     action: ["Delete", "Import", "Classification"],
//         //     type: ["Delete", "Create", "Info"],
//         //     severity: ["danger", "warning"]
//         // }
//         rej(e);
//       }
//     );
//   });
// }

const uploadActionPhoto = (imageFile) => {
  let url = `${CHATBOT_BASE_URL}/photos/add`;

  const formData = new FormData();
  formData.append("image", imageFile, imageFile.name);
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
  };
  return new Promise((resolve, reject) => {
    post(url, formData, config).then(
      (response) => {
        resolve(`${CHATBOT_BASE_URL}/photo/${response.data}`);
      },
      (e) => {
        reject(e);
      }
    );
  });
};

const getUserInfoFromAuth0Token = () => {
  const url = `${CONFIG_URL.AUTH0.USER_INFO}`;

  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        const { data } = resp;
        res(data);
        // const loginData = JSON.parse(getDecryptedItem("data"));
        // const dummyPermissionObj = {
        //   status: true,
        //   errorMessage: null,
        //   jsonData: "",
        //   data: null
        // };

        // const permissionData = {
        //     Workbench: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     IntElieDocs: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     SmartExtract_Builder: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     SmartExtract_Mapper: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     Orchestrator: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     SmartClasses: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     SmartQueue: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     AccessConsole: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     UserManager: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     OrganisationManager: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     APIKey: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     BillingCenter: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     BillingDashboard: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     History: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //     Subscription_Manager: {
        //       visibility: true,
        //       canCreate: true,
        //       canRead: true,
        //       canUpdate: true,
        //       canDelete: true,
        //     },
        //   }

        // if (permissionData) {
        //   if(loginData?.role === "ADMINISTRATOR") {
        //     permissionData.OrganisationManager.canCreate = false;
        //   }
        //   if (loginData?.role === "PARTNER") {
        //     permissionData.APIKey.visibility = false;
        //     permissionData.Workbench.visibility = false;
        //     permissionData.IntElieDocs.visibility = false;
        //     permissionData.Orchestrator.visibility = false;
        //     permissionData.SmartExtract_Mapper.visibility = false;
        //     permissionData.SmartExtract_Builder.visibility = false;
        //     permissionData.SmartClasses.visibility = false;
        //     permissionData.SmartQueue.visibility = false;
        //   }

        //   if (loginData?.role === "END_USER") {
        //     permissionData.UserManager.visibility = false;
        //     permissionData.OrganisationManager.visibility = false;
        //     permissionData.Orchestrator.visibility = false;
        //     permissionData.SmartExtract_Mapper.visibility = false;
        //     permissionData.SmartExtract_Builder.visibility = false;
        //     permissionData.SmartClasses.visibility = false;
        //     permissionData.SmartQueue.visibility = false;
        //   }

        //   if (loginData?.role === "PRODUCT_MANAGER") {
        //     permissionData.UserManager.visibility = false;
        //     permissionData.OrganisationManager.visibility = false;
        //   }

        //   if (loginData?.role === "DESIGNER") {
        //     permissionData.UserManager.visibility = false;
        //     permissionData.OrganisationManager.visibility = false;
        //   }
        // }
        // dummyPermissionObj.data = permissionData
        // // res(dummyPermissionObj);
      },
      (e) => {
        rej(e);
      }
    );
  });
};

const sendUserMessageToRASA = ({ chatbotId, message }) => {
  let userData = getDecryptedItem("data");

  try {
    userData = userData && JSON.parse(userData);
    const { email, organisationId, id: userId } = userData || {};
    const url = `${CHATBOT_BASE_URL}/webhook`;

    return new Promise((res, rej) => {
      post(url, {
        chatbotId,
        request: {
          message,
          sender: userId,
          organisationId: organisationId,
          email: email,
        },
      }).then(
        (resp) => {
          const { data } = resp;
          res(data);
        },
        (e) => {
          rej(e);
        }
      );
    });
  } catch (err) {
    console.log("catch err", err);
    return new Promise((res, rej) => {
      rej(err);
    });
  }
};

const getActionDetails = (actionId) => {
  const url = `${CHATBOT_BASE_URL}/action/get/${actionId}`;

  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        const { data } = resp;
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
};

const getSlotDetails = (slotId) => {
  const url = `${CHATBOT_BASE_URL}/slot/get/${slotId}`;

  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        const { data } = resp;
        res(data);
      },
      (e) => {
        rej(e);
      }
    );
  });
};

const uploadFile = (fileObject) => {
  const url = NEW_BASE_APPLICATION.POST;

  const formData = new FormData();
  formData.append("file", fileObject, fileObject.name);
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
  };
  return new Promise((resolve, reject) => {
    post(url, formData, config).then(
      (response) => {
        resolve(response.data);
      },
      (e) => {
        reject(e);
      }
    );
  });
};

// const exportEntityTypeTemplate = async (entityTypeObject) => {
//   const { id, name } = entityTypeObject;
//   const url = `${KNOWLEDGE_BASE_URL}/api/entities/${id}/template`;
//   const config = {
//     responseType: "blob",
//   };

//   return new Promise((res, rej) => {
//     get(url, config).then(
//       (response) => {
//         console.log("exportEntityTypeTemplate response", response);
//         if (response.status === 200) {
//           const link = document.createElement("a");
//           link.href = window.URL.createObjectURL(new Blob([response.data]), {
//             type: response.headers["content-type"],
//           });
//           link.setAttribute("download", `${name}-template.xlsx`);
//           document.body.appendChild(link);
//           link.click();
//           document.body.removeChild(link);
//           res(true);
//         } else {
//           alertCall("", "danger");
//           res(false);
//         }
//       },
//       (e) => {
//         console.log("exportEntityTypeTemplate catch e", e);
//         alertCall(e?.response?.data?.message, "danger");
//         rej(e);
//       }
//     );
//   });
// };

// const exportEntityTypeInstanceData = async (entityTypeObject) => {
//   const { id, name } = entityTypeObject;
//   const url = `${KNOWLEDGE_BASE_URL}/api/entities/${id}/data`;
//   const config = {
//     responseType: "blob",
//   };

//   return new Promise((res, rej) => {
//     get(url, config).then(
//       (response) => {
//         console.log("exportEntityTypeInstanceData response", response);
//         if (response.status === 200) {
//           const link = document.createElement("a");
//           link.href = window.URL.createObjectURL(new Blob([response.data]), {
//             type: response.headers["content-type"],
//           });
//           link.setAttribute("download", `${name}-data.xlsx`);
//           document.body.appendChild(link);
//           link.click();
//           document.body.removeChild(link);
//           res(true);
//         } else {
//           alertCall("", "danger");
//           res(false);
//         }
//       },
//       (e) => {
//         console.log("exportEntityTypeInstanceData catch e", e);
//         alertCall(e?.response?.data?.message, "danger");
//         rej(e);
//       }
//     );
//   });
// };

// const importEntityTypeInstanceData = async (entityTypeObject, excelFile) => {
//   const { id } = entityTypeObject;
//   const url = `${KNOWLEDGE_BASE_URL}/api/entities/${id}/data`;
//   const formData = new FormData();
//   formData.append("file", excelFile, excelFile.name);

//   const config = {
//     headers: {
//       "content-type": "multipart/form-data",
//     },
//   };

//   return new Promise((resolve, reject) => {
//     post(url, formData, config).then(
//       (response) => {
//         console.log("importEntityTypeInstanceData response", response);
//         if (response.status === 200) {
//           alertCall("Data imported successfully!", "success");
//           resolve(true);
//         } else {
//           alertCall("", "danger");
//           resolve(false);
//         }
//       },
//       (e) => {
//         console.log("importEntityTypeInstanceData catch e", e);
//         reject(e);
//         alertCall(e?.response?.data?.message, "danger");
//       }
//     );
//   });
// };

const getUserById = (userId) => {
  const url = `${AUTH0_BASE_URL}/api/v1/user/${userId}`;

  return new Promise((res, rej) => {
    get(url).then(
      (resp) => {
        res(resp.data);
      },
      (e) => {
        rej(e);
      }
    );
  });
};

const uploadFileOnAzureBlobStorage = (file, organisationId) => {
  const fileName = file.name;

  const formData = new FormData();
  formData.append("file", file, fileName);
  formData.append("organisationId", organisationId);

  let url = AZURE_BLOB_UPLOAD_URL + "/upload";
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
  };

  return new Promise((resolve, reject) => {
    post(url, formData, config).then(
      (response) => {
        const { status, data } = response?.data || {};
        if (status) {
          resolve(data);
        }
        reject(fileName);
      },
      (e) => {
        console.log("uploadFileOnAzureBlobStorage error", e);
        reject(fileName);
      }
    );
  });
};

const donwloadFileFromAzureBlobStorage = (fileId, fileName) => {
  let url = AZURE_BLOB_UPLOAD_URL + `/download/${fileId}`;

  return get(url, { responseType: "blob" }).then(
    (response) => {
      console.log("donwloadFileFromAzureBlobStorage response", response);
      const { data, headers } = response;
      const dataType = headers["content-type"];

      let downloadLink = document.createElement("a");
      const blob = new Blob([data], {
        type: dataType || "application/octet-stream",
      });
      downloadLink.href = window.URL.createObjectURL(blob);
      if (fileName) {
        downloadLink.setAttribute("download", fileName);
      }
      document.body.appendChild(downloadLink);
      downloadLink.click();

      setTimeout(() => {
        URL.revokeObjectURL(downloadLink.href);
        downloadLink.parentNode.removeChild(downloadLink);
      }, 0);
    },
    (e) => {
      console.log("donwloadFileFromAzureBlobStorage error", e);
      throw e;
    }
  );
};

const deleteFileOnAzureBlobStorage = (fileId) => {
  let url = AZURE_BLOB_UPLOAD_URL + "/delete/" + fileId;

  return new Promise((resolve, reject) => {
    deleteApi(url).then(
      (response) => {
        if (response.status === 200) {
          resolve(response.data);
        } else {
          reject(response);
        }
      },
      (e) => {
        reject(e);
      }
    );
  });
};

function getWorkqueueGroupDocuments(workqueueId) {
  let url = CONFIG_URL.WORKQUEUES.BASE_URL + `/${workqueueId}/group-documents`;

  return new Promise((resolve, reject) => {
    get(url).then(
      (response) => {
        const { status, data, jsonData, errorMessage } = response?.data || {};
        if (status) {
          resolve(data);
        } else {
          reject(
            errorMessage ||
            jsonData ||
            "Unable to check if workqueue is empty or not, please try again."
          );
        }
      },
      (e) => {
        reject(e?.message);
      }
    );
  });
}

function updateModelStatus({ id, state, devRemark = "" }) {
  let url = CONFIG_URL.MODEL.BASE_URL + `/${id}`;

  return new Promise((resolve, reject) => {
    post(url, { state, devRemark }).then(
      (response) => {
        const { status, jsonData, errorMessage } = response?.data || {};
        if (status) {
          resolve({ status, jsonData });
        }
        reject(jsonData || errorMessage);
      },
      (e) => {
        console.log("updateModelStatus error", e);
        reject(e.message);
      }
    );
  });
}

function logOut() {
  store.dispatch({ type: "USER_LOGOUT" });
  const themeConfig = getDecryptedItem("themeConfig");
  sessionStorage.clear();
  localStorage.clear();
  setEncryptedItem("themeConfig", themeConfig);
  window.location.href = "/login";
}

const forceLogOut = () => {
  logOut();
  setTimeout(() => {
    window.location.reload(true);
  }, 500);
};

export {
  get,
  post,
  put,
  deleteApi,
  getCollections,
  // getFilters,
  getDocuments,
  getDocument,
  // getEntities,
  // getMappings,
  uploadDoc,
  // updateEntities,
  updateMappings,
  deleteDocument,
  // getGroups,
  savePartner,
  getPartners,
  syncGoogleDrive,
  userLogin,
  deletePartner,
  uploadUserImg,
  removeUserImg,
  getOrgDetails,
  changePasswordApi,
  // getLogs,
  // getLogFilters,
  getUserInfoFromAuth0Token,
  uploadActionPhoto,
  sendUserMessageToRASA,
  getActionDetails,
  getSlotDetails,
  uploadFile,
  // exportEntityTypeTemplate,
  // exportEntityTypeInstanceData,
  // importEntityTypeInstanceData,
  getUserById,
  uploadFileOnAzureBlobStorage,
  donwloadFileFromAzureBlobStorage,
  deleteFileOnAzureBlobStorage,
  getWorkqueueGroupDocuments,
  updateModelStatus,
  logOut,
  forceLogOut,
};
